import { useState, useCallback, useMemo } from "react";
import type { Locale } from "date-fns";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { CalendarIcon, X } from "lucide-react";
import { subDays, subMonths, startOfMonth, startOfYear, endOfDay, isSameDay } from "date-fns";
import { cn } from "@/lib/utils";
import { DatePicker } from "@/components/ui/date-picker";
import { useTranslation } from "@/hooks/useTranslation";
import { getDateFnsLocale, safeFormatDate } from "@/lib/dateUtils";

// ============================================================================
// Types
// ============================================================================

export interface DateRange {
  from?: Date;
  to?: Date;
}

export type DateRangePreset =
  | 'mtd'           // Month to Date
  | 'ytd'           // Year to Date
  | 'last30'        // Last 30 Days
  | 'last90'        // Last 90 Days
  | 'last12Months'  // Last 12 Months — the current month plus the 11 whole months before it
  | 'lastFiscalYear' // Last Fiscal Year (calendar year)
  | 'thisMonth'     // This Month (same as mtd but different label)
  | 'lastMonth'     // Last Month
  | 'thisYear'      // This Year — the FULL calendar year (Jan 1 – Dec 31), unlike ytd
  | 'custom';       // Custom range

export interface DateRangeWithLabel extends DateRange {
  label: string;
}

export interface DateRangeFilterProps {
  value?: DateRange;
  onChange: (range: DateRange | undefined) => void;
  variant?: 'inline' | 'popover' | 'buttons';
  presets?: DateRangePreset[];
  showClear?: boolean;
  placeholder?: string;
  className?: string;
  /** When set, persists date range changes to localStorage under this key. */
  storageKey?: string;
}

// ============================================================================
// Preset Configuration
// ============================================================================

/**
 * Minimal translate signature — the `t` from `useTranslation()` (common
 * namespace) satisfies it. Keys live under `dateRange.*` in common.json.
 */
export type DateRangeTranslate = (key: string, options?: Record<string, unknown>) => string;

/** Which presets carry a compact `short` label (used by the buttons variant). */
const PRESETS_WITH_SHORT_LABEL: ReadonlySet<DateRangePreset> = new Set<DateRangePreset>([
  'mtd',
  'ytd',
  'last30',
  'last90',
  'last12Months',
  'lastFiscalYear',
]);

/**
 * English fallbacks for `getDateRangeFromPreset` when it is called WITHOUT a
 * `t` (non-React callers that only need the dates). Anything that DISPLAYS a
 * label must pass `t` — see `getPresetLabel`.
 */
const EN_FALLBACK_LABELS: Record<DateRangePreset, string> = {
  mtd: 'Month to Date',
  ytd: 'Year to Date',
  last30: 'Last 30 Days',
  last90: 'Last 90 Days',
  last12Months: 'Last 12 Months',
  lastFiscalYear: 'Last Fiscal Year',
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
  thisYear: 'This Year',
  custom: 'Custom',
};

/** Translated preset label; `short` picks the compact form where one exists. */
export function getPresetLabel(
  preset: DateRangePreset,
  t: DateRangeTranslate,
  { short = false }: { short?: boolean } = {},
): string {
  const variant = short && PRESETS_WITH_SHORT_LABEL.has(preset) ? 'short' : 'label';
  return t(`dateRange.presets.${preset}.${variant}`);
}

/** Options for `getDateRangeFromPreset`'s label: translate + date-fns locale. */
export interface DateRangeLabelOptions {
  t?: DateRangeTranslate;
  locale?: Locale;
}

const DEFAULT_PRESETS: Record<string, DateRangePreset[]> = {
  buttons: ['mtd', 'ytd', 'last30', 'last90', 'lastFiscalYear', 'custom'],
  inline: ['thisMonth', 'lastMonth', 'thisYear', 'lastFiscalYear', 'last30'],
  popover: ['thisMonth', 'lastMonth', 'thisYear', 'lastFiscalYear', 'last30'],
};

// ============================================================================
// LocalStorage Helpers
// ============================================================================

/** Read a persisted date range from localStorage. Returns undefined if missing or invalid. */
export function getStoredDateRange(storageKey: string): DateRange | undefined {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (parsed.from && parsed.to) {
      const from = new Date(parsed.from);
      const to = new Date(parsed.to);
      if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())) {
        return { from, to };
      }
    }
    // Corrupt or partial: CLEAR it so the consumer falls back to its default.
    // Returning Invalid Dates here throws RangeError in the first format()
    // call downstream — with no reset path, since the page dies before any
    // control that could overwrite the bad value renders.
    localStorage.removeItem(storageKey);
  } catch {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // storage unavailable – nothing to reset
    }
  }
  return undefined;
}

/** Write a date range to localStorage. */
export function setStoredDateRange(storageKey: string, range: DateRange): void {
  try {
    if (range.from && range.to) {
      localStorage.setItem(storageKey, JSON.stringify({
        from: range.from.toISOString(),
        to: range.to.toISOString(),
      }));
    }
  } catch {
    // storage full or unavailable – ignore
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the date range for a given preset.
 * Exported for use by consumers who need to compute date ranges externally.
 */
export function getDateRangeFromPreset(
  preset: DateRangePreset,
  customRange?: DateRange,
  { t, locale }: DateRangeLabelOptions = {},
): DateRangeWithLabel {
  const now = new Date();
  const today = endOfDay(now);
  const labelFor = (p: DateRangePreset) => (t ? getPresetLabel(p, t) : EN_FALLBACK_LABELS[p]);

  switch (preset) {
    case 'mtd':
    case 'thisMonth':
      return {
        from: startOfMonth(now),
        to: today,
        label: labelFor(preset),
      };
    case 'ytd':
      return {
        from: startOfYear(now),
        to: today,
        label: labelFor('ytd'),
      };
    case 'thisYear':
      // The FULL current calendar year, not a YTD alias: "This Year" must
      // include future-dated bookings (a December bill entered in August),
      // which is the whole point of offering it next to YTD.
      return {
        from: startOfYear(now),
        to: endOfDay(new Date(now.getFullYear(), 11, 31)),
        label: labelFor('thisYear'),
      };
    case 'last30':
      return {
        from: subDays(now, 30),
        to: today,
        label: labelFor('last30'),
      };
    case 'last90':
      return {
        from: subDays(now, 90),
        to: today,
        label: labelFor('last90'),
      };
    case 'last12Months':
      // Starts on a month boundary so a monthly chart shows 12 whole bars
      // (the current month to date is the twelfth).
      return {
        from: subMonths(startOfMonth(now), 11),
        to: today,
        label: labelFor('last12Months'),
      };
    case 'lastFiscalYear': {
      const lastYear = now.getFullYear() - 1;
      return {
        from: new Date(lastYear, 0, 1),  // Jan 1 of last year
        to: endOfDay(new Date(lastYear, 11, 31)),  // Dec 31 of last year
        label: t ? t('dateRange.fiscalYear', { year: lastYear }) : `FY ${lastYear}`,
      };
    }
    case 'lastMonth': {
      const lastMonthStart = subMonths(startOfMonth(now), 1);
      const lastMonthEnd = subDays(startOfMonth(now), 1);
      return {
        from: lastMonthStart,
        to: endOfDay(lastMonthEnd),
        label: labelFor('lastMonth'),
      };
    }
    case 'custom':
      if (customRange?.from && customRange?.to) {
        return {
          from: customRange.from,
          to: customRange.to,
          label: formatDateRange(customRange, t ?? englishRangeT, locale),
        };
      }
      // Fallback to YTD if no custom range
      return {
        from: startOfYear(now),
        to: today,
        label: labelFor('ytd'),
      };
    default:
      return {
        from: startOfYear(now),
        to: today,
        label: labelFor('ytd'),
      };
  }
}

/** English range templates for the no-`t` fallback of `getDateRangeFromPreset`. */
const englishRangeT: DateRangeTranslate = (key, options) => {
  const vars = options ?? {};
  if (key === 'dateRange.range') return `${vars.from} - ${vars.to}`;
  if (key === 'dateRange.dayPattern') return 'MMM d';
  if (key === 'dateRange.fromDate') return `From ${vars.date}`;
  if (key === 'dateRange.toDate') return `To ${vars.date}`;
  return key;
};

function formatDateRange(
  range: DateRange | undefined,
  t: DateRangeTranslate,
  locale?: Locale,
  placeholder?: string,
): string {
  // The day pattern is per language ("MMM d" in EN, "d MMM" in NL/FR); a
  // missing key (passthrough `t` in tests) falls back to the EN pattern.
  const pattern = t('dateRange.dayPattern');
  const dayPattern = pattern === 'dateRange.dayPattern' ? "MMM d" : pattern;
  const fmt = (d: Date) => safeFormatDate(d, dayPattern, "", locale);
  if (range?.from && range?.to) {
    return t('dateRange.range', { from: fmt(range.from), to: fmt(range.to) });
  }
  if (range?.from) return t('dateRange.fromDate', { date: fmt(range.from) });
  if (range?.to) return t('dateRange.toDate', { date: fmt(range.to) });
  return placeholder ?? t('dateRange.placeholder');
}

/** The common-namespace `t` plus the date-fns locale for the active language. */
function useDateRangeI18n(): { t: DateRangeTranslate; locale: Locale } {
  const { t, i18n } = useTranslation();
  const language = i18n?.language ?? 'en';
  const locale = useMemo(() => getDateFnsLocale(language), [language]);
  return { t: t as DateRangeTranslate, locale };
}

// ============================================================================
// Main Component
// ============================================================================

export function DateRangeFilter({
  value,
  onChange,
  variant = 'inline',
  presets,
  showClear = true,
  placeholder,
  className,
  storageKey,
}: DateRangeFilterProps) {
  const effectivePresets = presets ?? DEFAULT_PRESETS[variant];

  // Wrap onChange to persist to localStorage when storageKey is set
  const handleChange = useCallback(
    (range: DateRange | undefined) => {
      if (storageKey && range?.from && range?.to) {
        setStoredDateRange(storageKey, range);
      }
      onChange(range);
    },
    [storageKey, onChange]
  );

  if (variant === 'buttons') {
    return (
      <ButtonsVariant
        value={value}
        onChange={handleChange}
        presets={effectivePresets}
        showClear={showClear}
        className={className}
      />
    );
  }

  if (variant === 'popover') {
    return (
      <PopoverVariant
        value={value}
        onChange={handleChange}
        presets={effectivePresets}
        showClear={showClear}
        placeholder={placeholder}
        className={className}
      />
    );
  }

  // Default: inline variant
  return (
    <InlineVariant
      value={value}
      onChange={handleChange}
      presets={effectivePresets}
      showClear={showClear}
      className={className}
    />
  );
}

// ============================================================================
// Buttons Variant (for Accounting Overview)
// ============================================================================

interface VariantProps {
  value?: DateRange;
  onChange: (range: DateRange | undefined) => void;
  presets: DateRangePreset[];
  showClear: boolean;
  placeholder?: string;
  className?: string;
}

function ButtonsVariant({ value, onChange, presets, showClear, className }: VariantProps) {
  const { t, locale } = useDateRangeI18n();
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const [tempFrom, setTempFrom] = useState<Date | undefined>(value?.from);
  const [tempTo, setTempTo] = useState<Date | undefined>(value?.to);
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset | null>(null);

  const handlePresetClick = (preset: DateRangePreset) => {
    if (preset === 'custom') {
      setTempFrom(value?.from);
      setTempTo(value?.to);
      setIsCustomOpen(true);
    } else {
      const range = getDateRangeFromPreset(preset);
      setSelectedPreset(preset);
      onChange({ from: range.from, to: range.to });
    }
  };

  const handleCustomApply = () => {
    if (tempFrom && tempTo) {
      // Validate that from date is not after to date
      if (tempFrom > tempTo) {
        // Swap dates if from > to
        setSelectedPreset('custom');
        onChange({ from: tempTo, to: tempFrom });
      } else {
        setSelectedPreset('custom');
        onChange({ from: tempFrom, to: tempTo });
      }
      setIsCustomOpen(false);
    }
  };

  const handleCustomClear = () => {
    setTempFrom(undefined);
    setTempTo(undefined);
    // Default to YTD when clearing
    const ytdRange = getDateRangeFromPreset('ytd');
    setSelectedPreset('ytd');
    onChange({ from: ytdRange.from, to: ytdRange.to });
    setIsCustomOpen(false);
  };

  // Determine if a preset is currently active by comparing date ranges
  const isPresetActive = (preset: DateRangePreset): boolean => {
    if (preset === 'custom') {
      return selectedPreset === 'custom';
    }
    if (!value?.from || !value?.to) return false;

    const presetRange = getDateRangeFromPreset(preset);
    if (!presetRange.from || !presetRange.to) return false;

    // Compare dates using isSameDay for timezone-safe comparison
    const fromMatches = isSameDay(value.from, presetRange.from);
    const toMatches = isSameDay(value.to, presetRange.to);
    return fromMatches && toMatches;
  };

  return (
    <div className={cn("flex items-center gap-1", className)}>
      {presets.map((preset) => {
        const shortLabel = getPresetLabel(preset, t, { short: true });
        const isActive = isPresetActive(preset);

        if (preset === 'custom') {
          return (
            <Popover key={preset} open={isCustomOpen} onOpenChange={setIsCustomOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={isActive ? 'default' : 'outline'}
                  size="sm"
                  className="h-8"
                >
                  <CalendarIcon className="h-3.5 w-3.5 mr-1.5" />
                  {isActive && value?.from && value?.to
                    ? formatDateRange(value, t, locale)
                    : shortLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4" align="end">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{t('dateRange.from')}</Label>
                      <DatePicker
                        value={tempFrom}
                        onChange={setTempFrom}
                        className="w-full"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">{t('dateRange.to')}</Label>
                      <DatePicker
                        value={tempTo}
                        onChange={setTempTo}
                        className="w-full"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {showClear && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCustomClear}
                        className="flex-1"
                      >
                        <X className="h-3.5 w-3.5 mr-1" />
                        {t('dateRange.clear')}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={handleCustomApply}
                      disabled={!tempFrom || !tempTo}
                      className={showClear ? "flex-1" : "w-full"}
                    >
                      {t('dateRange.apply')}
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          );
        }

        return (
          <Button
            key={preset}
            variant={isActive ? 'default' : 'outline'}
            size="sm"
            onClick={() => handlePresetClick(preset)}
            className="h-8"
          >
            {shortLabel}
          </Button>
        );
      })}
    </div>
  );
}

// ============================================================================
// Inline Variant (for Data Grids)
// ============================================================================

function InlineVariant({ value, onChange, presets, showClear, className }: VariantProps) {
  const { t } = useDateRangeI18n();
  const handlePresetChange = (presetId: string) => {
    const preset = presets.find(p => p === presetId);
    if (preset && preset !== 'custom') {
      const range = getDateRangeFromPreset(preset);
      onChange({ from: range.from, to: range.to });
    }
  };

  const handleClear = () => {
    onChange(undefined);
  };

  // Filter out 'custom' from inline presets (custom is handled via date pickers)
  const selectPresets = presets.filter(p => p !== 'custom');

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {/* Quick Date Presets */}
      <Select onValueChange={handlePresetChange}>
        <SelectTrigger className="w-38">
          <SelectValue placeholder={t('dateRange.quickDate')} />
        </SelectTrigger>
        <SelectContent>
          {selectPresets.map(preset => (
            <SelectItem key={preset} value={preset}>
              {getPresetLabel(preset, t)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* From Date */}
      <DatePicker
        value={value?.from}
        onChange={(date) => onChange({ from: date, to: value?.to })}
        className="w-[150px]"
        placeholder={t('dateRange.from')}
      />

      {/* To Date */}
      <DatePicker
        value={value?.to}
        onChange={(date) => onChange({ from: value?.from, to: date })}
        className="w-[150px]"
        placeholder={t('dateRange.to')}
      />

      {/* Clear Button */}
      {showClear && (value?.from || value?.to) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="h-9 px-2"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">{t('dateRange.clearAria')}</span>
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// Popover Variant (for Compact Spaces)
// ============================================================================

function PopoverVariant({ value, onChange, presets, showClear, placeholder, className }: VariantProps) {
  const { t, locale } = useDateRangeI18n();
  const [isOpen, setIsOpen] = useState(false);

  const handlePresetChange = (presetId: string) => {
    const preset = presets.find(p => p === presetId);
    if (preset && preset !== 'custom') {
      const range = getDateRangeFromPreset(preset);
      onChange({ from: range.from, to: range.to });
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    onChange(undefined);
    setIsOpen(false);
  };

  // Filter out 'custom' from popover presets
  const selectPresets = presets.filter(p => p !== 'custom');

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value?.from && !value?.to && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {formatDateRange(value, t, locale, placeholder)}
          {showClear && (value?.from || value?.to) && (
            <X
              className="ml-auto h-4 w-4 hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-4 space-y-4">
          {/* Quick Date Presets */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('dateRange.quickDates')}</Label>
            <Select onValueChange={handlePresetChange}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder={t('dateRange.selectPeriod')} />
              </SelectTrigger>
              <SelectContent>
                {selectPresets.map(preset => (
                  <SelectItem key={preset} value={preset}>
                    {getPresetLabel(preset, t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Date Range */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('dateRange.from')}</Label>
              <DatePicker
                value={value?.from}
                onChange={(date) => onChange({ from: date, to: value?.to })}
                className="h-9"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('dateRange.to')}</Label>
              <DatePicker
                value={value?.to}
                onChange={(date) => onChange({ from: value?.from, to: date })}
                className="h-9"
              />
            </div>
          </div>

          {/* Clear Button */}
          {showClear && (value?.from || value?.to) && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClear}
              className="w-full"
            >
              {t('dateRange.clear')}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ============================================================================
// Legacy Export for Backwards Compatibility
// ============================================================================

/**
 * @deprecated Use DateRangeFilter with variant="inline" instead
 */
export function ExpandedDateRangeFilter(props: Omit<DateRangeFilterProps, 'variant'>) {
  return <DateRangeFilter {...props} variant="inline" />;
}
